import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@taxdown/zeno-sama-query';
import RecordsService from 'Services/records';
import { PueblaPreviousYearsResponse } from 'Services/sat/types';
import NotificationService, { AlertTypes } from 'Services/notification';
import i18n from 'Services/i18n';
import CheckoutService from 'Services/checkout';
import { history, store } from 'Store';
import { setStageAction } from 'Store/modules/validations/actions';
import { useGetQuery } from 'Hooks/useGetQuery';
import { routes } from 'Constants/routes';
import {
  CustomerType,
  QueryKeys,
  ResponseType,
  SubmitInfoFilingType,
  SubmitResultType,
} from 'Types/common';

export const getDataFilingUser = async (): Promise<SubmitInfoFilingType | unknown> => {
  const response = await RecordsService.getFiledInfo();
  if (response) {
    const { data } = response;
    return data.item;
  }
  return {};
};

export const useFetchFilingUser = (): UseQueryResult<SubmitInfoFilingType> =>
  useQuery([QueryKeys.FETCH_FILING_USERS], getDataFilingUser, {
    onError: () => {
      history.push(routes.DASHBOARD);
    },
    refetchOnWindowFocus: false,
    cacheTime: 1,
  });

export const usePatchFilingUser = (): UseMutationResult<unknown, unknown, unknown, unknown> =>
  useMutation([QueryKeys.PATCH_FILING_USERS], RecordsService.setFiledInfo, {
    onSuccess: () => {
      NotificationService.sendNotification(i18n.t('common:Patch.filing.user'), AlertTypes.success);
    },
    cacheTime: 0,
  });

export const usePostSubmitFiling = (
  changeStep: (val: number) => void
): UseMutationResult<ResponseType<SubmitResultType>> =>
  useMutation([QueryKeys.SUBMIT_DECLARATION], RecordsService.setSubmit, {
    onSuccess: data => {
      const response = data.data;
      store.dispatch(setStageAction(response.item.stage));
      changeStep(7);
    },
  });

export const useGetInfoFilingUser = (): SubmitInfoFilingType => {
  const response = useGetQuery([QueryKeys.FETCH_FILING_USERS]) as SubmitInfoFilingType;

  return response;
};

export const useGetPreviousYearInfo = (): PueblaPreviousYearsResponse | undefined => {
  const data = useGetQuery<ResponseType<PueblaPreviousYearsResponse>>([
    QueryKeys.GET_PREVIOUS_YEAR_RESULT,
  ]);
  if (!data) {
    return undefined;
  }
  const info = data?.data;
  return info?.item;
};

export const useSubscribeLiteFlex = (): UseMutationResult<
  ResponseType<unknown>,
  { taxYear: string; type: string }
> => {
  return useMutation([QueryKeys.SUBSCRIBE_FLEX_LITE], CheckoutService.subscribeLiteFlex);
};
export const useAutoRenewal = (): UseMutationResult<
  ResponseType<CustomerType>,
  { isAutoRenewalEnabled: boolean }
> => {
  const queryClient = useQueryClient();
  return useMutation([QueryKeys.AUTO_RENEWAL], CheckoutService.autoRenewal, {
    onSuccess: data => {
      queryClient.setQueryData([QueryKeys.FETCH_GET_CUSTOMERS], data);
    },
  });
};

export const useSubmitDeclarationCrossSell = (
  notIsPayLater?: boolean
): UseMutationResult<ResponseType<SubmitResultType>> =>
  useMutation([QueryKeys.SUBMIT_DECLARATION_CROSS_SELL], RecordsService.setSubmit, {
    onSuccess: data => {
      const response = data.data;
      store.dispatch(setStageAction(response.item.stage));
      if (notIsPayLater) {
        history.push(routes.PREVIOUS_YEARS, {
          isPrefilingUser: true,
        });
      } else {
        history.push(routes.PREVIOUS_YEARS_FILTER);
      }
    },
  });

export const useRenewalFlex = (): UseMutationResult<
  ResponseType<CustomerType>,
  { isAutoRenewalEnabled: boolean }
> => {
  const queryClient = useQueryClient();
  return useMutation([QueryKeys.RENEWAL_FLEX], CheckoutService.autoRenewal, {
    onSuccess: data => {
      queryClient.setQueryData([QueryKeys.FETCH_GET_CUSTOMERS], data);
    },
  });
};
